import type { AxisAndGridProps, SingleAxisProps } from './AxisAndGrid.types';
import { useLayoutEffect, useRef } from 'react';
import { initAxisAndGrid } from './js/initAxisAndGrid';
import { formatTickLabels } from './js/formatTickLabels';
import './css/axis-and-grid.css';

export const HtmlAxis = (props: SingleAxisProps): JSX.Element => {
  const { location, ticks = [], label, format, scale_type, time_zone, className, 'data-area': data_area } = props;
  const default_labels = formatTickLabels(ticks, { scale_type, time_zone });
  const vertical = location === 'left' || location === 'right';
  return (
    <div
      className={['axis', className].filter(Boolean).join(' ')}
      data-area={data_area ?? location}
      data-side={location}
    >
      {label !== undefined && <span className="html-axis-title">{label}</span>}
      <div className="html-axis-ticks">
        {ticks.map((tick, index) => {
          const label = format?.(tick, `tick-label-${location}`, { ...props });
          // Only false suppresses a tick; null and undefined fall back to its prepared label.
          if (label === false) return null;

          const position = tick.position;
          const endpoint = position.trim() === '0%' ? ' html-axis-tick--start'
            : position.trim() === '100%' ? ' html-axis-tick--end' : '';
          const className = `html-axis-tick html-axis-tick--${location}${endpoint}`;
          const tick_style = vertical ? { top: position } : { left: position };

          return (
            <span
              className={className}
              key={`${position}:${index}`}
              data-endpoint={
                position.trim() === '0%'
                  ? vertical
                    ? 'top'
                    : 'left'
                  : position.trim() === '100%'
                    ? vertical
                      ? 'bottom'
                      : 'right'
                    : undefined
              }
              style={tick_style}
            >
              {label ?? default_labels[index]} {tick?.unit || ''}
            </span>
          );
        })}
      </div>
    </div>
  );
};

/** Native scrolling keeps each rail aligned with its corresponding plot dimension. */
export const AxisAndGrid = ({
  axes = {},
  SVG,
  HTML,
  hover_ref,
  container_ref: supplied_container_ref,
  className,
  'data-area': data_area,
  time_zone,
  ...props
}: AxisAndGridProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const container_ref = supplied_container_ref || ref;

  useLayoutEffect(() => {
    const measure = () => initAxisAndGrid(container_ref);
    measure();
    const observer = new ResizeObserver(measure);
    if (container_ref.current) observer.observe(container_ref.current);
    document.fonts.addEventListener('loadingdone', measure);
    return () => {
      observer.disconnect();
      document.fonts.removeEventListener('loadingdone', measure);
    };
  }, [axes, container_ref]);

  const grid_positions = new Map<
    string,
    { direction: 'horizontal' | 'vertical'; position: string }
  >();
  for (const [side, axis] of Object.entries(axes)) {
    const direction = side === 'left' || side === 'right' ? 'horizontal' : 'vertical';
    for (const tick of axis?.ticks ?? []) {
      grid_positions.set(`${direction}:${tick.position}`, { direction, position: tick.position });
    }
  }
  const renderer_props = { ...props, time_zone, axes };

  return (
    <div
      ref={container_ref}
      data-area={data_area}
      className={['axis-and-grid', className].filter(Boolean).join(' ')}
      tabIndex={0}
      role="region"
      aria-label="Scrollable axes"
    >
      <div
        className="axis-and-grid-layout"
        data-left={Boolean(axes.left)}
        data-right={Boolean(axes.right)}
        data-top={Boolean(axes.top)}
        data-bottom={Boolean(axes.bottom)}
      >
        <div data-area="main" className="axis-and-grid-plot" ref={hover_ref}>
          <div className="axis-grid-lines" aria-hidden="true">
            {[...grid_positions].map(([key, line]) => {
              return (
                <span
                  key={key}
                  className="axis-grid-line"
                  data-direction={line.direction}
                  style={
                    line.direction === 'horizontal'
                      ? { top: line.position }
                      : { left: line.position }
                  }
                />
              );
            })}
          </div>
          {Array.isArray(SVG) && (
            <svg
              className="axis-and-grid-renderer axis-and-grid-renderer--svg"
              width="100%"
              height="100%"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              {SVG.map((Renderer, index) => (
                <Renderer key={index} {...renderer_props} />
              ))}
            </svg>
          )}
          {Array.isArray(HTML) && (
            <div className="axis-and-grid-renderer axis-and-grid-renderer--html">
              {HTML.map((Renderer, index) => (
                <Renderer key={index} {...renderer_props} />
              ))}
            </div>
          )}
        </div>
        {axes?.left && (
          <HtmlAxis
            location="left"
            {...axes?.left || {}}
            time_zone={time_zone}
          />
        )}
        {axes?.right && (
          <HtmlAxis
            location="right"
            {...axes?.right || {}}
            time_zone={time_zone}
          />
        )}
        {axes?.top && (
          <HtmlAxis
            location="top"
            {...axes?.top || {}}
            time_zone={time_zone}
          />
        )}
        {axes?.bottom && (
          <HtmlAxis
            location="bottom"
            {...axes?.bottom || {}}
            time_zone={time_zone}
          />
        )}
        {axes?.top && axes?.left && <div className="axis-corner" data-corner="top-left" />}
        {axes?.top && axes?.right && <div className="axis-corner" data-corner="top-right" />}
        {axes?.bottom && axes?.left && <div className="axis-corner" data-corner="bottom-left" />}
        {axes?.bottom && axes?.right && <div className="axis-corner" data-corner="bottom-right" />}
      </div>
    </div>
  );
};
